import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { withStyles } from '@material-ui/core'

import Modal from '../../../uiKit/Modal'
import { QCS_DEV_OVERRIDE_KEY } from '../../../constants/qcs'
import { QCS_DEV_ENABLED } from '../../../constants/devFlags'

/**
 * Development-only: mark organisations as QCS before the backend can.
 *
 * The QCS split is driven by a flag the backend sets at creation. Until that
 * ships there is no way to see any of the QCS experience on real data, and
 * asking someone to hand-write bot ids into `localStorage` is a poor substitute
 * for a control.
 *
 * Presented as a secondary button beside "New QCS organisation"; the picker —
 * search + a single-column list of selectable cards + apply — lives in a modal.
 * It writes the same local override key and nothing else — it never calls the
 * backend, so it cannot change what a real customer can reach. The whole
 * component is compiled out of production builds.
 */

const styles = () => ({
  // Text-only CTA — no border or fill, just a primary-coloured link that sits
  // level with the 48px "New QCS organisation" and underlines on hover.
  trigger: {
    height: 48,
    padding: '0 4px',
    boxSizing: 'border-box',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    background: 'transparent',
    border: 'none',
    fontFamily: 'var(--careco-font)',
    fontSize: 14,
    fontWeight: 600,
    color: 'var(--careco-primary)',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    textDecoration: 'none',
    transition: 'color 120ms ease',
    '&:hover': { textDecoration: 'underline', color: 'var(--careco-primary-hover)' },
    '&:focus-visible': {
      outline: '2px solid var(--careco-primary)',
      outlineOffset: 2,
    },
  },
  dialogPaper: {
    width: 560,
    maxWidth: '100%',
    padding: '26px 28px 22px',
  },
  // Left-aligned header, close ✕ stays top-right (drawn by the Modal shell).
  header: {
    paddingRight: 28,
    marginBottom: 'var(--careco-space-lg)',
  },
  title: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    margin: 0,
    fontFamily: 'var(--careco-font)',
    fontSize: 18,
    fontWeight: 700,
    lineHeight: 1.2,
    color: 'var(--careco-text-primary)',
  },
  badge: {
    padding: '2px 8px',
    borderRadius: 'var(--careco-rounded-pill, 999px)',
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: 0.4,
    color: 'var(--careco-status-inactive)',
    background: 'var(--careco-divider)',
  },
  intro: {
    margin: '8px 0 0',
    fontSize: 13,
    lineHeight: '19px',
    color: 'var(--careco-text-muted)',
    textAlign: 'left',
  },
  search: {
    width: '100%',
    height: 44,
    boxSizing: 'border-box',
    padding: '0 var(--careco-space-md)',
    border: '1px solid var(--careco-border)',
    borderRadius: 'var(--careco-rounded-sm)',
    fontFamily: 'var(--careco-font)',
    fontSize: 14,
    color: 'var(--careco-text-primary)',
    outline: 'none',
    '&::placeholder': { color: 'var(--careco-text-muted)' },
    '&:hover': { borderColor: 'var(--careco-primary)' },
    '&:focus': { borderColor: 'var(--careco-primary)' },
  },
  sectionLabel: {
    margin: '18px 0 8px',
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: 0.3,
    textTransform: 'uppercase',
    color: 'var(--careco-text-muted)',
  },
  // Single-column, scrollable list of cards.
  list: {
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--careco-space-xs, 8px)',
    margin: 0,
    padding: '2px 4px 2px 2px',
    listStyle: 'none',
    maxHeight: 300,
    overflowY: 'auto',
  },
  // Compact selectable card: white surface, subtle stroke, very soft elevation.
  item: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    minHeight: 46,
    boxSizing: 'border-box',
    padding: '0 14px',
    border: '1px solid var(--careco-border)',
    borderRadius: 'var(--careco-rounded-sm, 8px)',
    background: 'var(--careco-surface)',
    boxShadow: 'var(--careco-shadow-card, 0 2px 10px 2px rgba(110,110,110,0.06))',
    cursor: 'pointer',
    transition: 'border-color 120ms ease, box-shadow 120ms ease',
    '&:hover': {
      boxShadow: 'var(--careco-shadow-navigation, 0 4px 4px 3px rgba(110,110,110,0.06))',
    },
  },
  // Selected: predominantly white, distinguished by a primary stroke and the
  // filled checkbox — no heavy fill.
  itemSelected: {
    borderColor: 'var(--careco-primary)',
  },
  // Visually-hidden native input keeps the real checkbox semantics/keyboard
  // behaviour; the styled box below is what the user sees.
  hiddenInput: {
    position: 'absolute',
    opacity: 0,
    width: 1,
    height: 1,
    margin: 0,
    pointerEvents: 'none',
  },
  box: {
    flexShrink: 0,
    // Explicit margin, not flex `gap`, for the checkbox-to-text spacing.
    marginRight: 4,
    width: 18,
    height: 18,
    boxSizing: 'border-box',
    border: '1.5px solid var(--careco-border)',
    borderRadius: 5,
    background: 'var(--careco-surface)',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'var(--color-text-on-primary, #fff)',
    transition: 'background 120ms ease, border-color 120ms ease',
  },
  boxChecked: {
    background: 'var(--careco-primary)',
    borderColor: 'var(--careco-primary)',
  },
  check: {
    display: 'block',
  },
  itemName: {
    flex: '1 1 auto',
    minWidth: 0,
    fontSize: 14,
    fontWeight: 500,
    color: 'var(--careco-text-primary)',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  // Fixed right column so ids line up regardless of name length.
  itemId: {
    flexShrink: 0,
    marginLeft: 12,
    minWidth: 44,
    textAlign: 'right',
    fontSize: 12,
    color: 'var(--careco-text-muted)',
    fontVariantNumeric: 'tabular-nums',
  },
  empty: {
    padding: '24px 8px',
    textAlign: 'center',
    fontSize: 13.5,
    color: 'var(--careco-text-muted)',
  },
  footer: {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--careco-space-sm)',
    marginTop: 'var(--careco-space-md)',
    paddingTop: 'var(--careco-space-md)',
    borderTop: '1px solid var(--careco-border)',
  },
  note: {
    fontSize: 13,
    color: 'var(--careco-text-muted)',
    fontVariantNumeric: 'tabular-nums',
  },
  spacer: { flex: 1 },
  ghost: {
    height: 40,
    border: '1px solid var(--careco-border)',
    background: 'transparent',
    borderRadius: 'var(--careco-rounded-sm)',
    padding: '0 16px',
    fontFamily: 'var(--careco-font)',
    fontSize: 13.5,
    fontWeight: 600,
    color: 'var(--careco-text-primary)',
    cursor: 'pointer',
    '&:hover': { background: 'var(--careco-row-default)' },
  },
  primary: {
    height: 40,
    border: 'none',
    borderRadius: 'var(--careco-rounded-sm)',
    padding: '0 20px',
    fontFamily: 'var(--careco-font)',
    fontSize: 13.5,
    fontWeight: 600,
    color: 'var(--color-text-on-primary, #fff)',
    background: 'var(--careco-primary)',
    cursor: 'pointer',
    '&:hover': { background: 'var(--careco-primary-hover)' },
    '&:disabled': { opacity: 0.5, cursor: 'default' },
  },
})

const readOverride = () => {
  try {
    const parsed = JSON.parse(window.localStorage.getItem(QCS_DEV_OVERRIDE_KEY) || '[]')
    return Array.isArray(parsed) ? parsed.map(Number) : []
  } catch {
    return []
  }
}

const QcsDevFlagPanel = ({ classes, bots, onChange }) => {
  const [open, setOpen] = useState(false)
  const [selected, setSelected] = useState(readOverride)
  const [query, setQuery] = useState('')

  // After the hooks, never before: an early return above them would make the
  // hook order conditional. `NODE_ENV` is fixed at build time, so the guard is
  // still what strips this from production bundles.
  if (!QCS_DEV_ENABLED) return null

  const openModal = () => {
    // Re-read the stored selection each time, so the modal always opens on the
    // current state even if it was cleared elsewhere.
    setSelected(readOverride())
    setQuery('')
    setOpen(true)
  }

  const toggleBot = id =>
    setSelected(prev => (prev.includes(id) ? prev.filter(entry => entry !== id) : [...prev, id]))

  const persist = ids => {
    try {
      window.localStorage.setItem(QCS_DEV_OVERRIDE_KEY, JSON.stringify(ids))
    } catch {
      // Nothing to recover from — the panel is a development affordance.
    }
    setSelected(ids)
    onChange()
  }

  const apply = () => {
    persist(selected)
    setOpen(false)
  }

  // Lucide-style check glyph (the repo ships no lucide package, so the icon is
  // drawn inline from the same path lucide's `check` uses).
  const checkbox = checked => (
    <span className={`${classes.box} ${checked ? classes.boxChecked : ''}`} aria-hidden="true">
      {checked && (
        <svg className={classes.check} width="12" height="12" viewBox="0 0 24 24" fill="none">
          <path
            d="M20 6 9 17l-5-5"
            stroke="currentColor"
            strokeWidth="3"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      )}
    </span>
  )

  const sorted = [...(bots || [])].sort((a, b) => (a.name || '').localeCompare(b.name || ''))
  const term = query.trim().toLowerCase()
  const filtered = term
    ? sorted.filter(
        bot => (bot.name || '').toLowerCase().includes(term) || String(bot.id).includes(term),
      )
    : sorted

  return (
    <>
      <button type="button" className={classes.trigger} onClick={openModal}>
        Mark organisations as QCS
      </button>

      {open && (
        <Modal
          open={open}
          onClose={() => setOpen(false)}
          classes={{ paper: classes.dialogPaper }}>
          <div className={classes.header}>
            <h2 className={classes.title}>
              Mark organisations as QCS
              <span className={classes.badge}>DEV ONLY</span>
            </h2>
            <p className={classes.intro}>
              The backend does not set the QCS flag yet. Search and tick organisations to include
              them in the QCS listing. Stored in your browser only — it changes nothing for anyone
              else and is stripped from production builds.
            </p>
          </div>

          <input
            className={classes.search}
            type="text"
            placeholder="Search organisations"
            value={query}
            autoFocus
            onChange={event => setQuery(event.target.value)}
          />

          <div className={classes.sectionLabel}>Organisations</div>
          <ul className={classes.list}>
            {filtered.map(bot => {
              const isSelected = selected.includes(Number(bot.id))
              return (
                <li key={bot.id}>
                  <label className={`${classes.item} ${isSelected ? classes.itemSelected : ''}`}>
                    <input
                      type="checkbox"
                      className={classes.hiddenInput}
                      checked={isSelected}
                      onChange={() => toggleBot(Number(bot.id))}
                    />
                    {checkbox(isSelected)}
                    <span className={classes.itemName} title={bot.name}>
                      {bot.name}
                    </span>
                    <span className={classes.itemId}>#{bot.id}</span>
                  </label>
                </li>
              )
            })}
            {filtered.length === 0 && (
              <li className={classes.empty}>No organisations match “{query.trim()}”.</li>
            )}
          </ul>

          <div className={classes.footer}>
            <span className={classes.note}>{selected.length} selected</span>
            <span className={classes.spacer} />
            <button type="button" className={classes.ghost} onClick={() => persist([])}>
              Clear all
            </button>
            <button type="button" className={classes.primary} onClick={apply}>
              Apply
            </button>
          </div>
        </Modal>
      )}
    </>
  )
}

QcsDevFlagPanel.propTypes = {
  classes: PropTypes.object,
  bots: PropTypes.array,
  onChange: PropTypes.func,
}

export default withStyles(styles)(QcsDevFlagPanel)
